import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Info, ArrowLeft, ChevronLeft, ChevronRight, MessageSquare, Sparkles, StickyNote, X, Search } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost, apiPatch, apiDelete, apiUpload } from '../../utils/api';
import TeamMessageList from './TeamMessageList';
import TeamMessageInput from './TeamMessageInput';
import TypingIndicator from './TypingIndicator';
import ConversationSettings from './ConversationSettings';
import TeamAiSidebar from './TeamAiSidebar';
import toast from 'react-hot-toast';
import { modelServicePost } from '../../utils/modelService';
import AddToNotesModal from '../notes/AddToNotesModal';
import CreateTaskModal from '../tasks/CreateTaskModal';
import ViewNoteModal from '../notes/ViewNoteModal';

function ShareNotePicker({ notes, onSelect, onClose, theme }) {
    const [search, setSearch] = useState('');
    const isDark = theme === 'dark';
    const filtered = notes.filter(n => {
        if (!search) return true;
        const q = search.toLowerCase();
        return n.title?.toLowerCase().includes(q) || n.content?.toLowerCase().includes(q);
    });

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className={`w-full max-w-sm rounded-xl shadow-xl max-h-[70vh] flex flex-col ${isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2">
                        <StickyNote size={16} className="text-blue-500" />
                        <h3 className="font-dm-serif font-semibold text-sm">Share a Note</h3>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"><X size={16} /></button>
                </div>
                <div className={`px-4 py-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                        <Search size={14} className="text-gray-400" />
                        <input
                            type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search notes..." autoFocus
                            className={`bg-transparent outline-none text-sm font-rasa flex-1 ${isDark ? 'placeholder-gray-500' : 'placeholder-gray-400'}`}
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {filtered.length === 0 ? (
                        <p className={`text-center py-8 text-sm font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {notes.length === 0 ? 'No notes yet' : 'No matching notes'}
                        </p>
                    ) : filtered.map(note => (
                        <button
                            key={note.id}
                            onClick={() => onSelect(note.id)}
                            className={`w-full text-left px-4 py-3 border-b transition-colors ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center gap-2">
                                {note.color && note.color !== '#FFFFFF' && (
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: note.color }} />
                                )}
                                <span className="font-unna font-medium text-sm truncate">{note.title || 'Untitled'}</span>
                            </div>
                            <p className={`text-xs font-rasa mt-0.5 line-clamp-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {(note.content || '').replace(/<[^>]+>/g, '').substring(0, 80)}
                            </p>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function ConversationView({
    conversation,
    onConversationUpdated,
    onBack,
    teamChatEvents,
    sendTyping,
    sendStoppedTyping,
    markRead,
    sidebarOpen,
    onToggleSidebar
}) {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showAiSidebar, setShowAiSidebar] = useState(false);
    const [aiSidebarContext, setAiSidebarContext] = useState(null);
    const [typingUsers, setTypingUsers] = useState([]);
    const [showSaveToNotes, setShowSaveToNotes] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [showCreateTask, setShowCreateTask] = useState(false);
    const [taskPrefill, setTaskPrefill] = useState({});
    const [showShareNote, setShowShareNote] = useState(false);
    const [userNotes, setUserNotes] = useState([]);
    const [viewNoteId, setViewNoteId] = useState(null);

    const handleCreateTaskFromMessage = async (content) => {
        // Try AI extraction first
        try {
            const response = await modelServicePost('/extract-tasks', { content, source: 'team_chat' });
            if (response.ok) {
                const data = await response.json();
                if (data.tasks?.length > 0) {
                    const task = data.tasks[0]; // Use the first extracted task
                    setTaskPrefill({
                        title: task.title,
                        description: task.description || content,
                        priority: task.priority,
                        category: task.category,
                        source_type: 'team_chat',
                        source_label: 'Team Chat',
                    });
                    setShowCreateTask(true);
                    return;
                }
            }
        } catch { /* fallback to manual */ }

        // Fallback: use raw content
        setTaskPrefill({
            description: content,
            source_type: 'team_chat',
            source_label: 'Team Chat',
        });
        setShowCreateTask(true);
    };
    const [insertText, setInsertText] = useState('');
    const typingTimeoutsRef = useRef({});

    const isConversationAdmin = conversation?.members?.find(m => m.id === currentUser?.id)?.role === 'admin';

    // Insert message in chronological order (prevents race condition between API response and WebSocket)
    const addMessageSorted = useCallback((prev, newMsg) => {
        if (prev.some(m => m.id === newMsg.id)) return prev;
        const updated = [...prev, newMsg];
        updated.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        return updated;
    }, []);

    const fetchMessages = useCallback(async (before = null) => {
        if (!conversation?.id) return;

        if (before) {
            setLoadingMore(true);
        } else {
            setLoadingMessages(true);
        }

        try {
            const url = before
                ? `/conversations/${conversation.id}/messages?before=${before}&limit=50`
                : `/conversations/${conversation.id}/messages?limit=50`;

            const response = await apiGet(url);
            if (response.ok) {
                const data = await response.json();
                if (before) {
                    setMessages(prev => [...data.messages, ...prev]);
                } else {
                    setMessages(data.messages);
                }
                setHasMore(data.has_more);
            }
        } catch (err) {
            console.error('Failed to fetch messages:', err);
        } finally {
            setLoadingMessages(false);
            setLoadingMore(false);
        }
    }, [conversation?.id]);

    useEffect(() => {
        setMessages([]);
        setReplyTo(null);
        setShowSettings(false);
        setShowAiSidebar(false);
        setAiSidebarContext(null);
        fetchMessages();
        markRead?.(conversation?.id);
    }, [conversation?.id]);

    useEffect(() => {
        if (!teamChatEvents || !conversation) return;

        const event = teamChatEvents;
        if (event.conversation_id !== conversation.id) return;

        switch (event.type) {
            case 'new_message':
                setMessages(prev => addMessageSorted(prev, event.message));
                // Clear typing indicator when the user's actual message arrives
                if (event.message?.user?.id) {
                    setTypingUsers(prev => prev.filter(u => u.id !== event.message.user.id));
                    if (typingTimeoutsRef.current[event.message.user.id]) {
                        clearTimeout(typingTimeoutsRef.current[event.message.user.id]);
                    }
                }
                markRead?.(conversation.id);
                break;

            case 'message_edited':
                setMessages(prev => prev.map(m =>
                    m.id === event.message.id ? event.message : m
                ));
                break;

            case 'message_deleted':
                setMessages(prev => prev.filter(m => m.id !== event.message_id));
                break;

            case 'reaction_added':
            case 'reaction_removed':
                setMessages(prev => prev.map(m =>
                    m.id === event.message_id ? { ...m, reactions: event.reactions } : m
                ));
                break;

            case 'user_typing':
                setTypingUsers(prev => {
                    if (prev.some(u => u.id === event.user.id)) return prev;
                    return [...prev, { ...event.user, _isBot: event.user.is_bot }];
                });
                if (typingTimeoutsRef.current[event.user.id]) {
                    clearTimeout(typingTimeoutsRef.current[event.user.id]);
                }
                // Bot typing persists until their message arrives or stopped_typing is received.
                // Human typing expires after 5s as a fallback (they may close the tab).
                if (!event.user.is_bot) {
                    typingTimeoutsRef.current[event.user.id] = setTimeout(() => {
                        setTypingUsers(prev => prev.filter(u => u.id !== event.user.id));
                    }, 5000);
                }
                break;

            case 'user_stopped_typing':
                setTypingUsers(prev => prev.filter(u => u.id !== event.user.id));
                if (typingTimeoutsRef.current[event.user.id]) {
                    clearTimeout(typingTimeoutsRef.current[event.user.id]);
                }
                break;
        }
    }, [teamChatEvents, conversation?.id, markRead]);

    useEffect(() => {
        return () => {
            Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
        };
    }, []);

    const handleSendMessage = async ({ content, reply_to_id, files }) => {
        // Add optimistic message immediately so it appears in the chat before the API responds
        const tempId = `temp-${Date.now()}`;
        const optimisticMessage = {
            id: tempId,
            content,
            message_type: 'text',
            edited: false,
            created_at: new Date().toISOString(),
            user: {
                id: currentUser?.id,
                username: currentUser?.username,
                first_name: currentUser?.firstName,
                last_name: currentUser?.lastName,
                avatar_url: currentUser?.avatarUrl,
                is_bot: false
            },
            reply_to: replyTo ? {
                id: replyTo.id,
                content: replyTo.content?.substring(0, 100),
                user: replyTo.user
            } : null,
            reactions: [],
            attachments: []
        };
        setMessages(prev => addMessageSorted(prev, optimisticMessage));

        if (files && files.length > 0) {
            const formData = new FormData();
            formData.append('content', content);
            if (reply_to_id) formData.append('reply_to_id', reply_to_id);
            files.forEach(file => formData.append('attachments[]', file));

            const response = await apiUpload(`/conversations/${conversation.id}/messages`, formData);
            if (response.ok) {
                const data = await response.json();
                if (data.message) {
                    // Replace optimistic message with real one
                    setMessages(prev => {
                        const withoutTemp = prev.filter(m => m.id !== tempId);
                        return addMessageSorted(withoutTemp, data.message);
                    });
                }
            } else {
                // Remove optimistic message on failure
                setMessages(prev => prev.filter(m => m.id !== tempId));
            }
            return response.ok;
        }

        const response = await apiPost(`/conversations/${conversation.id}/messages`, {
            content,
            reply_to_id
        });
        if (response.ok) {
            const data = await response.json();
            if (data.message) {
                // Replace optimistic message with real one
                setMessages(prev => {
                    const withoutTemp = prev.filter(m => m.id !== tempId);
                    return addMessageSorted(withoutTemp, data.message);
                });
            }
        } else {
            // Remove optimistic message on failure
            setMessages(prev => prev.filter(m => m.id !== tempId));
        }
        return response.ok;
    };

    const handleEditMessage = async (messageId, content) => {
        await apiPatch(`/conversations/${conversation.id}/messages/${messageId}`, { content });
    };

    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm('Delete this message?')) return;
        await apiDelete(`/conversations/${conversation.id}/messages/${messageId}`);
    };

    const handleToggleReaction = async (messageId, emoji) => {
        await apiPost(`/conversations/${conversation.id}/messages/${messageId}/reactions`, { emoji });
    };

    const handleLoadMore = () => {
        if (messages.length > 0 && hasMore && !loadingMore) {
            fetchMessages(messages[0].id);
        }
    };

    if (!conversation) {
        return (
            <div className={`flex-1 flex flex-col ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                {!sidebarOpen && (
                    <div className={`flex items-center px-4 py-3 border-b flex-shrink-0 ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                        <button
                            onClick={onToggleSidebar}
                            className={`p-1.5 rounded-lg transition-colors ${
                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}
                            title="Show conversations"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
                <div className="flex-1 flex flex-col items-center justify-center">
                    <MessageSquare size={48} className={`mb-4 ${
                        theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
                    }`} />
                    <p className={`text-sm font-rasa ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>Select a conversation to start messaging</p>
                </div>
            </div>
        );
    }

    if (conversation.membership_status === 'pending') {
        const acceptInvite = async () => {
            try {
                const response = await apiPost(`/conversations/${conversation.id}/accept`);
                const data = await response.json();
                if (response.ok && data.conversation) {
                    onConversationUpdated?.(data.conversation);
                }
            } catch (err) {
                console.error('Failed to accept invite', err);
            }
        };
        const declineInvite = async () => {
            try {
                const response = await apiPost(`/conversations/${conversation.id}/decline`);
                if (response.ok) {
                    onBack?.();
                }
            } catch (err) {
                console.error('Failed to decline invite', err);
            }
        };

        return (
            <div className={`flex-1 flex flex-col ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className={`max-w-md w-full rounded-lg p-6 text-center border ${
                        theme === 'dark' ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
                    }`}>
                        <h2 className="text-lg font-dm-serif font-semibold mb-2">
                            You've been invited to join {conversation.title}.
                        </h2>
                        {conversation.inviter && (
                            <p className={`text-sm font-rasa mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                Invited by {conversation.inviter.first_name} {conversation.inviter.last_name}
                            </p>
                        )}
                        <p className={`text-sm font-rasa mb-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {conversation.members?.length || 0} member{(conversation.members?.length || 0) !== 1 ? 's' : ''}
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={declineInvite}
                                className={`px-4 py-2 border rounded font-rasa ${
                                    theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                Decline
                            </button>
                            <button
                                onClick={acceptInvite}
                                className="px-4 py-2 bg-blue-500 text-white rounded font-rasa hover:bg-blue-600"
                            >
                                Accept
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative flex flex-1 h-full overflow-hidden">
            <div className={`flex flex-col flex-1 min-w-0 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                {/* Header */}
                <div className={`flex items-center gap-3 px-4 py-3 border-b flex-shrink-0 ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    {/* Sidebar toggle — desktop */}
                    <button
                        onClick={onToggleSidebar}
                        className={`hidden md:flex p-1.5 rounded-lg transition-colors ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                        title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
                    >
                        {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                    </button>

                    {/* Back button — mobile */}
                    {onBack && (
                        <button
                            onClick={onBack}
                            className={`p-1.5 rounded-lg md:hidden ${
                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}
                        >
                            <ArrowLeft size={18} />
                        </button>
                    )}

                    <div className="flex-1 min-w-0">
                        <h2 className="text-sm font-unna font-semibold truncate">{conversation.display_name}</h2>
                        <p className={`text-xs font-rasa ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            {conversation.members?.length || 0} member{(conversation.members?.length || 0) !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            if (showAiSidebar) {
                                setShowAiSidebar(false);
                                setAiSidebarContext(null);
                            } else {
                                setShowSettings(false);
                                setShowAiSidebar(true);
                                setAiSidebarContext({ type: 'chat' });
                            }
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                            showAiSidebar
                                ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                                : theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                        }`}
                        title="JPRO Assistant"
                    >
                        <Sparkles size={18} />
                    </button>
                    <button
                        onClick={async () => {
                            try {
                                const response = await apiGet('/notes');
                                if (response.ok) {
                                    const data = await response.json();
                                    setUserNotes(data.notes || []);
                                }
                            } catch { /* silent */ }
                            setShowShareNote(true);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                            theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                        }`}
                        title="Share a note"
                    >
                        <StickyNote size={18} />
                    </button>
                    <button
                        onClick={() => {
                            setShowSettings(!showSettings);
                            if (!showSettings) {
                                setShowAiSidebar(false);
                                setAiSidebarContext(null);
                            }
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                            showSettings
                                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                : theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                        }`}
                    >
                        <Info size={18} />
                    </button>
                </div>

                {/* Messages */}
                {loadingMessages ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                ) : (
                    <TeamMessageList
                        messages={messages}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                        onLoadMore={handleLoadMore}
                        onReply={setReplyTo}
                        onEdit={handleEditMessage}
                        onDelete={handleDeleteMessage}
                        onToggleReaction={handleToggleReaction}
                        isConversationAdmin={isConversationAdmin}
                        onAiAction={(type, messageId, messageContent) => {
                            setShowSettings(false);
                            setShowAiSidebar(true);
                            setAiSidebarContext({ type, messageId, messageContent });
                        }}
                        onSaveToNotes={(content) => { setNoteContent(content); setShowSaveToNotes(true); }}
                        onCreateTask={handleCreateTaskFromMessage}
                        onViewNote={(noteId) => setViewNoteId(noteId)}
                    />
                )}

                <TypingIndicator typingUsers={typingUsers} />

                <TeamMessageInput
                    conversationId={conversation.id}
                    replyTo={replyTo}
                    onCancelReply={() => setReplyTo(null)}
                    onSendMessage={handleSendMessage}
                    onTyping={() => sendTyping?.(conversation.id)}
                    onStoppedTyping={() => sendStoppedTyping?.(conversation.id)}
                    insertText={insertText}
                    onInsertTextConsumed={() => setInsertText('')}
                    members={conversation.members}
                />
            </div>

            {/* Settings panel */}
            {showSettings && (
                <ConversationSettings
                    conversation={conversation}
                    onClose={() => setShowSettings(false)}
                    onUpdated={onConversationUpdated}
                />
            )}

            {/* AI sidebar */}
            {showAiSidebar && (
                <TeamAiSidebar
                    conversation={conversation}
                    context={aiSidebarContext}
                    chatMessages={messages}
                    onClose={() => {
                        setShowAiSidebar(false);
                        setAiSidebarContext(null);
                    }}
                    onInsertReply={(text) => setInsertText(text)}
                />
            )}
            {showSaveToNotes && (
                <AddToNotesModal
                    content={noteContent}
                    sourceType="team_chat"
                    onClose={() => setShowSaveToNotes(false)}
                    theme={theme}
                />
            )}
            {showShareNote && (
                <ShareNotePicker
                    notes={userNotes}
                    onSelect={async (noteId) => {
                        setShowShareNote(false);
                        try {
                            const response = await apiPost(`/conversations/${conversation.id}/messages`, {
                                content: noteId,
                                message_type: 'note',
                            });
                            if (response.ok) {
                                const data = await response.json();
                                setMessages(prev => addMessageSorted(prev, data.message));
                            }
                        } catch { /* silent */ }
                    }}
                    onClose={() => setShowShareNote(false)}
                    theme={theme}
                />
            )}
            {showCreateTask && (
                <CreateTaskModal
                    onClose={() => setShowCreateTask(false)}
                    onSubmit={async (taskData) => {
                        try {
                            const response = await apiPost('/tasks', taskData);
                            if (response.ok) toast.success('Task created');
                        } catch { /* silent */ }
                        setShowCreateTask(false);
                    }}
                    theme={theme}
                    prefill={taskPrefill}
                />
            )}
            {viewNoteId && (
                <ViewNoteModal
                    noteId={viewNoteId}
                    onClose={() => setViewNoteId(null)}
                    theme={theme}
                />
            )}
        </div>
    );
}
